import { IonToast } from '@ionic/react';
import { useEffect, useRef, useState } from 'react';
import { CoreToast } from '../../store/core/coreSlice.interface';
import { resetError, selectCoreToast } from '../../store/core/coreSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';

export const ErrorToast = () => {
  const dispatch = useAppDispatch();
  const coreToast = useAppSelector(selectCoreToast);
  // `useIonToast()`'s imperative present()/dismiss() controller resolves present() before the
  // toast element finishes hydrating, so a fast dismiss()+present() cycle can land mid-hydration
  // and crash Stencil with "Cannot add property C, object is not extensible". The declarative
  // <IonToast isOpen> component avoids that controller entirely.
  //
  // <IonToast> itself is never conditionally unmounted (no `if (!hasContent) return null`, no
  // `key` remount) -- it stays mounted for the app's lifetime and is only ever toggled via
  // `isOpen`. Ionic's overlay components manage their own DOM lifecycle (present/dismiss
  // animations, portal placement) internally; force-unmounting/remounting the React element
  // while that's in flight -- e.g. tapping the toast's own close button, whose `onDidDismiss`
  // used to synchronously clear `header`/`msg` and unmount the component -- races React's
  // reconciler against Ionic's own cleanup for the same DOM node and throws "Failed to execute
  // 'removeChild': The node to be removed is not a child of this node."
  //
  // A new toast that arrives while one is still open/animating is queued and only shown once
  // `onDidDismiss` fires for the current one, which also gives it a fresh enter animation and
  // duration without ever touching the mounted state of <IonToast>.
  const [isOpen, setIsOpen] = useState(false);
  const [displayed, setDisplayed] = useState<CoreToast | null>(null);
  const pendingRef = useRef<CoreToast | null>(null);

  useEffect(() => {
    if (!coreToast?.show) return;

    if (isOpen) {
      pendingRef.current = coreToast;
      setIsOpen(false);
    } else {
      setDisplayed(coreToast);
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coreToast]);

  const handleDidDismiss = () => {
    dispatch(resetError());
    setIsOpen(false);

    if (pendingRef.current) {
      const next = pendingRef.current;
      pendingRef.current = null;
      setDisplayed(next);
      setIsOpen(true);
    }
  };

  const duration = displayed?.duration || (displayed?.isError ? 4000 : 2000);

  return (
    <IonToast
      isOpen={isOpen}
      header={displayed?.header || ''}
      message={displayed?.msg || ''}
      mode="ios"
      duration={duration}
      position={displayed?.position || 'top'}
      cssClass={`toast-background ${displayed?.isError ? 'text-danger' : ''}`}
      onDidDismiss={handleDidDismiss}
      buttons={[
        {
          text: 'X',
          role: 'cancel',
        },
      ]}
    />
  );
};
